/* eslint-disable max-len */
/* eslint-disable dot-notation */
/* eslint-disable consistent-return */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useRouter } from 'next/router';
import TagManager from 'react-gtm-module';
import dynamic from 'next/dynamic';
import axios from 'axios';
import NProgress from 'nprogress';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import find from 'lodash/find';
import * as selectors from '@src/store/selectors';
import * as actions from '@src/store/actions';
import reduxWrapper from '@src/store';
import { reactLocalStorage } from '@src/utils/localStorage';
import {
	checkIsServer,
	getApiData,
	getTrustedSchoolHostname,
} from '@src/utils/deprecatedCommon';
import * as offlinePunchScheduler from '@src/utils/punch/offlinePunchScheduler';
import * as firestorePunchWatcher from '@src/utils/punch/firestorePunchWatcher';
import { setPunchTaskWatcherAdapter } from '@src/utils/punch/firestoreWatcherAdapter';
import usePunchInRetryOutcomeListener from '@src/hooks/punch/usePunchInRetryOutcomeListener';
import useHasOutstandingAsyncPunch from '@src/hooks/punch/useHasOutstandingAsyncPunch';
import { setMachinePunchError } from '@src/store/actions/punchIn';
import { isDev, isStaging, isInLineBrowser } from '@src/utils/env';
import { colorThemes } from '@src/const/theme';
import config from '@src/config';
import { defaultActionCalls } from '@src/router/ssr';
import { resolveRedirectTarget } from '@src/const/redirectConfigs';
import App from '@src/App';
import i18n from '@src/i18n';
import MyToastContainer from '@src/components/MyToastContainer';
import useAppLanguage from '@src/hooks/useAppLanguage';
import { isHrLanguageRoute, isLanguageTransitionRoute } from '@src/i18n/languagePolicy';
import BasicSeo from '@src/components/seo/basicSeo';
import FBMessenger from '@src/components/seo/basicSeo/fbMessenger';
import NoIndex from '@src/components/seo/basicSeo/noIndex';
import PreviewCover from '@src/components/common/previewCover';
import device from '@src/styles/breakpoint';
import FBPixelCode from '@src/components/seo/basicSeo/fbPixelCode';
import { recordIssueStop } from '@src/store/actions/session';
import GlobalLoading from '@src/components/common/GlobalLoading';
import { PUNCH_MACHINE_DEMO_PATH, isPunchMachinePath } from '@src/components/hr/punchInMachine/constants';
import ErrorPage from '@src/components/ErrorPage';
import ErrorIcon from '@src/components/common/icons/ErrorIcon';
import '@ncdai/react-wheel-picker/dist/index.css';

import '@src/styles/styles.scss';

const MachinePunchErrorResult = dynamic(() => import('@src/components/hr/punchInMachine/components/ErrorResult'), { ssr: false });

const GlobalStyle = createGlobalStyle`
	.ant-notification {
		width: 100%;
		pointer-events: none;
		z-index: 100000 !important;
	}
	.pwa-toast {
		box-shadow: unset !important;
		padding: unset !important;
		background: rgba(0, 0, 0, 0) !important;
		width: unset !important;
		display: flex;
		justify-content: center;
		& .ant-notification-notice-content {
			padding: 12px 20px;
			background: #222;
			border-radius: 8px;
			display: flex;
			justify-content: center;
			align-items: center;
			pointer-events: auto;
		}
		& .ant-notification-notice-message {
			width: fit-content;
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			line-height: 22px;
			color: #FFFFFF;
			padding: unset;
			margin: unset;
			padding-right: 0px !important;
		}
		& .ant-notification-notice-close {
			display: none;
		}
	}
`;

const RecordIssueToast = styled.div`
	position: fixed;
	padding: 12px 20px;
	background: #222;
	color: #FFF;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 22px;
	border-radius: 8px;
	bottom: 120px;
	z-index: 9999;
	left: calc(50% - 160px + 115px);
	@media ${props => props.theme.device.mobile} {
		left: calc(50% - 160px);
	}
	.close {
		color: #3091FD;
		padding-left: 10px;
		cursor: pointer;
		:hover {
			color: #005ABE;
		}
	}
`;

const saveAffiliateParams = ({ key, value }) => {
	if (value) {
		reactLocalStorage.set(key, value);
	}
};

// 關閉特定警告，避免拖慢速度
const closeConsoleWarn = () => {
	const IGNORED_WARNINGS = [
		'Warning: componentWillReceiveProps has been renamed',
		'Warning: componentWillUpdate has been renamed',
		'Warning: componentWillMount',
		'ResizeObserver loop limit exceeded',
	];
	const oldConsoleWarn = console.warn;

	console.warn = (...args) => {
		if (
			typeof args[0] === 'string' &&
      IGNORED_WARNINGS.some(ignored => args[0].startsWith(ignored))
		) {
			return;
		}

		return oldConsoleWarn.apply(console, args);
	};

	// error

	const IGNORED_ERRORS = ['Warning: Text content did not match. Server'];

	const oldConsoleError = console.error;

	console.error = (...args) => {
		if (
			typeof args[0] === 'string' &&
      IGNORED_ERRORS.some(ignored => args[0].startsWith(ignored))
		) {
			return;
		}

		return oldConsoleError.apply(console, args);
	};
};

closeConsoleWarn();

const { isTest, useSentry } = config;

const NotMatchPage = dynamic(() => import('@src/pages/NotMatchPage'), {
	ssr: false,
});

if (!checkIsServer()) {
	// Setup API Domain in Client Side
	// 用絕對 URL (同源) 避免任何相對路徑在深頁面 (如 /hr/overtime/[id]/edit) 底下被解析成奇怪的路徑；也比對 course-web/src/config/pro.js 的保守寫法。
	// Dev 下同源 URL 會進到 Next.js dev server 的 /api 代理 (pages/api/[...slug].js)；
	// Prod 下 ingress 會直接把 /api/* 送到後端，此檔案 handler 不會被觸發。
	axios.defaults.baseURL = `${window.location.origin}/api/`;
}

// 開機時判斷是否需要用 refresh_token cookie 換新 access_token。
// JWT access_token 不再存 localStorage，只存 Redux memory → 重新整理後記憶體消失，需重新 refresh。
const needsBootRefresh = () => {
	if (typeof window === 'undefined') return false;
	const signedIn = reactLocalStorage.get('isSignedIn');
	const jwt = reactLocalStorage.get('isJwtToken');
	const signedInTrue = signedIn === true || signedIn === 'true';
	const jwtTrue = jwt === true || jwt === 'true';

	return signedInTrue && jwtTrue;
};

const isPublicPage = path => path.indexOf('/business') === -1 &&
		path.indexOf('/action') === -1 &&
		path.indexOf('/preview') === -1 &&
		path.indexOf('/p/') === -1 &&
		path.indexOf('/category/') === -1 &&
		path.indexOf('/event/[eventShortLink]/[id]') === -1 &&
		path.indexOf('/jobs/[id]') === -1 &&
		path.indexOf('/qrcode') === -1 &&
		path.indexOf('/hr/registration-link') === -1 &&
		path.indexOf('/demo/') === -1 &&
		path.indexOf('/r/') === -1;

const getBusinessLoginPath = () => {
	const { pathname, href } = window.location;
	if (pathname === '/training' || pathname === '/' || pathname === '/hr') return '/business-login';
	return `/business-login?cb=${encodeURIComponent(href)}`;
};

function MyApp(props) {
	// 預設值是給「client-side 導航到 SSG 頁面（如 /hr）」用的：那種情況 Next 不會跑 _app.getInitialProps，
	// MyApp 收到的 props 只有 Component / pageProps，沒有 domain / ssrHeaders / statusCode。
	// statusCode 若是 undefined 會被下面的 `statusCode !== 200` 判成錯誤頁（伺服器忙碌中），所以一定要 fallback 200。
	const { Component, pageProps, domain = '', ssrHeaders = {}, statusCode = 200 } = props;

	const languageReady = useAppLanguage();
	const router = useRouter();
	const isPunchMachineDemo = router.pathname === PUNCH_MACHINE_DEMO_PATH;
	const dispatch = useDispatch();
	const { themes } = useSelector(selectors.getThemeSelector);
	const themeState = useSelector(selectors.getThemeState);
	const isRecordIssue = useSelector(state => state.session?.isRecordIssue);
	const [isInitialLoading, setIsInitialLoading] = useState(true);
	// 初始設 true 避免 SSR / hydration mismatch；client useEffect 決定是否真的需要 refresh。
	const [isBooting, setIsBooting] = useState(true);
	const bootStartedRef = useRef(false);
	// GTM 只能 init 一次（重複呼叫會重複注入 script）；用 ref 而非 state 因為不需要觸發 re-render。
	const gtmInitedRef = useRef(false);
	const {
		gtm_id, closed_web,
		fb_page_id, fb_chat_plugin_enabled, noindex, fb_pixel_id,
	} = themes;
	const isSignedIn = reactLocalStorage.get('isSignedIn');
	const { previewToken, previewCallback } = router.query;

	const isPreviewPage = () => !!previewToken;

	const isNotLoginCloseWeb = closed_web &&
		(isSignedIn === 'false' || isSignedIn === null) &&
		isPublicPage(router.pathname) && !isPreviewPage();

	useEffect(() => {
		if ((isDev || isStaging) && isInLineBrowser()) {
			import('vconsole').then(({ default: VConsole }) => {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const vConsole = new VConsole();
			});
		}
	}, []);

	// 瀏覽器 trace：production 取樣 1%，dev / staging 取樣 100%
	useEffect(() => {
		import('@src/utils/browserTrace').then(({ initBrowserTrace }) => initBrowserTrace());
	}, []);

	// Client 端 Sentry：延後到掛載後動態載入，讓 @sentry/react 不進每頁共用 chunk
	useEffect(() => {
		if (!useSentry) return undefined;
		let cancelled = false;
		import('@sentry/react').then(Sentry => {
			if (cancelled) return;
			Sentry.init({
				dsn: 'https://b41ef2adfc9440d8a18db17d9556558e@o455515.ingest.sentry.io/5525113',
				release: process.env.SENTRY_RELEASE,
			});
			const token = reactLocalStorage.get('token');
			Sentry.setContext('header', { token: `${token}` });
		});
		return () => { cancelled = true; };
	}, []);

	// 在 client 端確保 theme 一定有被抓過：themeState 只要回到 'IDLE' 就補一發 getThemesCall。
	// 兩個會撞到 'IDLE' 的情境：
	//   1. SSG 頁面（如 /hr）—— build 時 _app.getInitialProps 因 ctx.req 是 mock（沒 host header）早退、沒做 SSR theme fetch；
	//      SSG 頁面 client 端 hydration 也不會再跑 getInitialProps → themeState 一直是 'IDLE'。
	//   2. 任意頁面打 API 撞 401 → interceptor refresh 失敗 → LOGOUT_DONE 把整個 store reset 回 initialState → themeState 又變 'IDLE'。
	// 兩種情況沒補抓的話 state.theme.showPageLoading 會卡在 true，AppBackground 一路 render null（白頁）。
	// disableLoading 讓畫面先用預設 theme 立刻 paint，theme 回來再套用；SSR 過、themeState 已是 SUCCESS/FAIL/NOT_FOUND 時是 no-op。
	// 例外：正式打卡機與 /demo/punch-in-machine，兩者都不吃 theme。
	useEffect(() => {
		if (themeState === 'IDLE' && !isPunchMachinePath(router.pathname)) {
			dispatch(actions.getThemesCall({ temporary: isPreviewPage() ? previewToken : undefined, disableLoading: true }));
		}
	}, [themeState, router.pathname]);

	// 開機 boot-time refresh：若是 JWT 使用者，先用 refresh_token cookie 換新 access_token 再放行 checkLoginCall，
	// 避免 Redux memory 空的瞬間導致 checkLoginCall 走 401 → interceptor → refresh 的繞路。
	useEffect(() => {
		// Demo 不初始化 session；第一次離開 demo 才啟動，之後不因切頁重複 refresh。
		if (isPunchMachineDemo || bootStartedRef.current) return;
		bootStartedRef.current = true;
		if (typeof window === 'undefined') {
			setIsBooting(false);
			return;
		}

		if (!needsBootRefresh()) {
			setIsBooting(false);
			return;
		}

		axios.post('user/refresh', null, {
			headers: { Authorization: '' },
			isRefresh: true,
		})
			.then(res => {
				const token = res?.data?.access_token;
				if (!token) {
					throw new Error('refresh response missing access_token');
				}
				dispatch(actions.setToken({ token, isJwtToken: true }));
			})
			.catch(err => {
				// 區分失敗原因，只有「真的 auth 失效」(401/403) 才 logout
				const status = err?.response?.status;
				const isAuthFailure = status === 401 || status === 403;
				if (isAuthFailure) {
					dispatch(actions.logoutDone());
				}
			})
			.finally(() => {
				setIsBooting(false);
			});
  }, [isPunchMachineDemo]); // eslint-disable-line

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const setViewportHeight = () => {
			requestAnimationFrame(() => {
				const vh = window.innerHeight * 0.01;
				document.documentElement.style.setProperty('--vh', `${vh}px`);
			});
		};

		setViewportHeight();

		window.addEventListener('resize', setViewportHeight);
		window.addEventListener('orientationchange', setViewportHeight);

		return () => {
			window.removeEventListener('resize', setViewportHeight);
			window.removeEventListener('orientationchange', setViewportHeight);
		};
	}, []);

	useEffect(() => {
		const { action } = router.query || {};
		if (action === 'handle_conflict_email') {
			dispatch(actions.setModalType({ type: 'conflictEmail' }));
		}
	}, [router]); // eslint-disable-line

	useEffect(() => {
		const { fromCompanyVat } = router.query || {};
		if (fromCompanyVat && !isSignedIn && !closed_web) {
			dispatch(actions.setModalType({ type: 'login' }));
		}
	}, [router, closed_web, isSignedIn]); // eslint-disable-line

	useEffect(() => {
		const { query: {
			track_id,
			utm_campaign,
			utm_medium,
			utm_source,
			valid_until,
		} } = router;
		saveAffiliateParams({ key: 'track_id', value: track_id });
		saveAffiliateParams({ key: 'utm_campaign', value: utm_campaign });
		saveAffiliateParams({ key: 'utm_medium', value: utm_medium });
		saveAffiliateParams({ key: 'utm_source', value: utm_source });
		saveAffiliateParams({ key: 'valid_until', value: valid_until });
	}, []);

	// GTM init：獨立 effect、deps 含 gtm_id。/hr 改 SSG 後 themes 是 client 端才抓的，
	// gtm_id 在「等 boot refresh 完」那個 effect 跑的當下通常還沒到 → 留在那會永遠 miss（那 effect deps 只有 isBooting，不會重跑）。
	// gtmInitedRef 防止 gtm_id 之後又變動時重複注入 GTM script。
	useEffect(() => {
		if (isBooting || isTest || !gtm_id || gtmInitedRef.current) return;
		gtmInitedRef.current = true;
		TagManager.initialize({ gtmId: gtm_id }); // PS. gtm id setting is on the dashboard.
	}, [isBooting, gtm_id]);

	useEffect(() => {
		// 等 boot refresh 跑完再決定登入檢查流程，確保 Redux 的 access_token 已就緒（或已確定失敗）。
		if (isPunchMachineDemo || isBooting) return;
		const { fromCompanyVat: fcv, forceRelink } = router.query || {};
		if (isPreviewPage()) {
			dispatch(actions.setPreviewToken({
				token: previewToken,
			}));
			setIsInitialLoading(false);
		} else if (isSignedIn && isSignedIn !== 'false' && !router.pathname.startsWith('/business-login')) {
			dispatch(actions.checkIsSignedIn());
			dispatch(actions.checkLoginCall({
				fromLogin: false,
				ssrHeaders,
				onSuccess: () => {
					if (fcv && forceRelink) {
						dispatch(actions.forceLinkRichMenuCall({ hr_line_id_token: fcv }));
					}
					setIsInitialLoading(false);
				},
				onError: () => {
					if (fcv) {
						// session 過期但有 LINE token，嘗試自動登入
						dispatch(actions.lineTokenLoginCall({
							hr_line_id_token: fcv,
							...(forceRelink ? { force_link_rich_menu: true } : {}),
							onSuccess: () => setIsInitialLoading(false),
							onError: () => setIsInitialLoading(false),
						}));
					} else {
						setIsInitialLoading(false);
					}
				},
			}));
		} else {
			// 未登入狀態
			if (!isPreviewPage()) reactLocalStorage.remove('previewToken');
			const redirectToBusinessLogin = () => {
				router.push(getBusinessLoginPath());
				setIsInitialLoading(false);
			};
			// closed_web 企業未登入一律導去企業登入；/hr* 額外無條件納入：/hr 改 SSG 後 themes.closed_web 要等 client 抓完 theme 才有，
			// 這個 effect 跑在那之前 —— 不提前導的話未登入者會先 render 進 /hr → HR 元件打 API 撞 401 繞一圈 → theme 回來才 isNotLoginCloseWeb=true → 卡在空白頁、也沒被導去登入。
			// 已知所有 /hr 企業都是 closed_web，所以這裡提前導向是安全的；非 /hr 的頁面行為不變（closed_web 已在 SSR state 裡）。
			if ((closed_web || router.pathname.startsWith('/hr')) && isPublicPage(router.pathname)) {
				if (fcv) {
					// 沒有 session 但有 LINE token，嘗試自動登入
					dispatch(actions.lineTokenLoginCall({
						hr_line_id_token: fcv,
						...(forceRelink ? { force_link_rich_menu: true } : {}),
						onSuccess: () => setIsInitialLoading(false),
						onError: redirectToBusinessLogin,
					}));
				} else {
					redirectToBusinessLogin();
				}
			} else {
				setIsInitialLoading(false);
			}
		}
  }, [isBooting, isPunchMachineDemo]); // eslint-disable-line

	useEffect(() => {
		if (isPunchMachineDemo || isBooting || isInitialLoading) return;
		if (!isNotLoginCloseWeb) return;

		router.replace(getBusinessLoginPath());
	}, [isBooting, isInitialLoading, isNotLoginCloseWeb, router.pathname, isPunchMachineDemo]); // eslint-disable-line

	// Animation for changing page url
	useEffect(() => {
		const handleRouteChangeStart = url => {
			NProgress.configure({ showSpinner: false });
			NProgress.start();
		};
		router.events.on('routeChangeStart', handleRouteChangeStart);

		const handleRouteChangeComplete = url => {
			NProgress.done();
		};
		router.events.on('routeChangeComplete', handleRouteChangeComplete);

		return () => {
			router.events.off('routeChangeStart', handleRouteChangeStart);
			router.events.off('routeChangeComplete', handleRouteChangeComplete);
		};
  }, []); // eslint-disable-line

	// 離線打卡 retry scheduler：在 /hr/* 任何子頁、有登入的使用者，背景跑 IDB queue retry。
	// 註：原本這裡有 Service Worker 註冊 / HR_CHUNKS allowlist 收集 / USER_CHANGED 通知三段，
	//     已隨「改用 app 層 API 回應快取（src/db/apiResponseCache.ts）+ CDN 接手靜態」一起移除。
	//     多帳號隔離由 apiResponseCache 以 accountId 分區處理，不需要在這裡通知清 cache。
	const currentUserId = useSelector(selectors.getUserId);
	const reduxStore = useStore();
	// Demo 不補送真實紀錄、不監聽結果；切回 HR 時由 effect 重新啟動。
	const isOnHrRoute = !isPunchMachineDemo && router.pathname.startsWith('/hr');
	useEffect(() => {
		if (typeof currentUserId !== 'string' || !currentUserId) return undefined;
		if (!isOnHrRoute) return undefined;
		offlinePunchScheduler.start(currentUserId, reduxStore);
		return () => offlinePunchScheduler.stop();
	}, [currentUserId, isOnHrRoute, reduxStore]);

	// 非同步打卡（server queue）啟用條件：
	//   - use_server_queue：punch-in-info 回的租戶開關（DB 控灰度，免上版），只決定「新的打卡」走 enqueue 或同步 API
	//   - hasOutstandingAsyncPunch：IDB 還有在途的 202 紀錄 → 即使開關已關（降級）或還沒載到，watcher 仍要啟動把在途紀錄收尾
	// 兩者皆 false 的租戶（從未開啟）不會動態載入 firebase chunk。
	const useServerQueue = useSelector(state => state.punchIn?.punchIn?.use_server_queue === true);
	const hasOutstandingAsyncPunch = useHasOutstandingAsyncPunch(
		!isPunchMachineDemo && typeof currentUserId === 'string' && currentUserId ? currentUserId : undefined,
	);
	const asyncPunchEnabled = !isPunchMachineDemo && (useServerQueue || hasOutstandingAsyncPunch);

	const firebaseAdapterRequestedRef = useRef(false);
	useEffect(() => {
		if (!asyncPunchEnabled || firebaseAdapterRequestedRef.current) return undefined;
		firebaseAdapterRequestedRef.current = true;
		// 注入是全域冪等操作，條件在 import 在飛時翻回 false 也照樣完成注入（無害，resync 未 start 時 no-op）；
		// 若在此取消，requested ref 已置 true 會擋掉之後的重試，watcher 將永遠停在 no-op stub
		import('@src/utils/firebase/firestoreFirebaseAdapter')
			.then(m => {
				setPunchTaskWatcherAdapter(m.firestoreFirebaseAdapter);
				// watcher 的 start 可能先跑、監聽已掛在 no-op stub 上（收不到結果）→ 注入後全拆重建
				firestorePunchWatcher.resync();
			})
			.catch(() => {
				// 載入失敗維持 no-op stub；清掉 requested 讓下次條件變動有機會重試
				firebaseAdapterRequestedRef.current = false;
			});
		return undefined;
	}, [asyncPunchEnabled]);

	useEffect(() => {
		if (!asyncPunchEnabled) return undefined;
		if (typeof currentUserId !== 'string' || !currentUserId) return undefined;
		if (!isOnHrRoute) return undefined;
		firestorePunchWatcher.start(currentUserId, reduxStore);
		return () => firestorePunchWatcher.stop();
	}, [asyncPunchEnabled, currentUserId, isOnHrRoute, reduxStore]);

	// subscribe local queue retry：限定 /hr/* 才消化（toast 在非 HR 頁面出現會讓使用者困惑），未消化的留在 Redux 等回 /hr/* 再處理
	// isHomePage（首頁打卡頁）決定非同步打卡失敗時要開 modal 還是只 toast
	usePunchInRetryOutcomeListener(isOnHrRoute, router.pathname === '/hr');
	const machinePunchError = useSelector(state => state.punchIn?.machinePunchError);

	if (statusCode !== 200) {
		if (statusCode === 404) {
			return <NotMatchPage />;
		} else {
			return (
				<ErrorPage title="Error" messages={['伺服器忙碌中', '請重整頁面或稍後再試。']}>
					<ErrorIcon />
				</ErrorPage>
			);
		}
	}

	const themeName = themes.color_theme;
	const selectedTheme = find(colorThemes, theme => theme.id === themeName) || colorThemes[0];
	const themeObj = {
		device,
		colors: selectedTheme?.colors,
	};
	// Keep login/HR pages and authenticated training pages hidden until their final language is applied.
	const authenticated = isSignedIn === true || isSignedIn === 'true';
	const waitingForLanguage = !isLanguageTransitionRoute(router.pathname) && (isHrLanguageRoute(router.pathname) || authenticated) && !languageReady;
	const showLoading = !isPunchMachineDemo && (isInitialLoading || isNotLoginCloseWeb || waitingForLanguage);

	return (
		<I18nextProvider i18n={i18n}>
			<ThemeProvider theme={themeObj}>
				{/* Keep toasts mounted across layout changes and language loading during login. */}
				<MyToastContainer />
				{showLoading ? <GlobalLoading /> : (
					<>
						<GlobalStyle />
						<FBMessenger id={fb_page_id} enable={fb_chat_plugin_enabled} />
						<NoIndex enable={noindex} />
						{fb_pixel_id && <FBPixelCode pixelId={fb_pixel_id} />}
						{
							!isNotLoginCloseWeb && (
								<App className="app-container">
									<BasicSeo domain={domain} />
									{previewToken && <PreviewCover callbackUrl={previewCallback} />}
									<Component themeName={themeName} {...pageProps} />
									{isOnHrRoute && machinePunchError?.accountId === currentUserId && (
										<MachinePunchErrorResult type={machinePunchError.errorType} onClose={() => dispatch(setMachinePunchError(null))} />
									)}
									{
										isRecordIssue && (
											<RecordIssueToast>
												請再操作一次發現 Bug 的完整流程
												<span
													className="close"
													onClick={() => {
														dispatch(recordIssueStop());
													}}
												>
													結束回報
												</span>
											</RecordIssueToast>
										)
									}
								</App>
							)
						}
					</>
				)}
			</ThemeProvider>
		</I18nextProvider>
	);
}

MyApp.getInitialProps = async props => {
	const { Component, ctx } = props;
	const { store, req, res = {} } = ctx;
	const statusCode = res?.statusCode ?? 200;

	// 三種情況跳過 SSR-only 流程（theme fetch / baseURL / NOT_FOUND redirect）：
	//   1. client-side navigation —— theme 已在 Redux
	//   2. build 時靜態生成（有 getStaticProps 的頁面，如 /hr，以及 Next 自動產的 /404）
	//      —— 此時 ctx.req 是 mock 物件，沒有 ingress 注入的 school hostname
	//   3. 直接存取 service/NodePort —— 只有任意 Host，不具備可信的學校 context
	// Dev localhost 沒有 ingress，因此不會有 x-school-hostname；但 NEXT_PUBLIC_HOSTNAME 是開發者明確指定的
	// school context，仍須繼續執行 getApiData 並初始化 axios baseURL，否則相對 API URL 會誤打 localhost:80。
	const hasConfiguredDevSchoolContext = isTest && !!process.env.NEXT_PUBLIC_HOSTNAME;
	if (
		!checkIsServer() ||
		!req ||
		(!getTrustedSchoolHostname(req) && !hasConfiguredDevSchoolContext)
	) {
		return {
			pageProps: Component.getInitialProps ?
				await Component.getInitialProps(ctx) :
				{},
			domain: store.getState().ssr?.domain || '',
			ssrHeaders: {},
			statusCode,
			ssrIsMobile: false,
		};
	}

	const { apiDomain, ssrHeaders, domain, ssrIsMobile } = getApiData(req);

	// 只在 SSR 階段覆蓋 baseURL。
	// Client-side navigation 時 Next.js 9 也會執行此 getInitialProps，若在 client 端覆蓋會把 module 開頭設的 '/api' (dev proxy 用) 或 window.location.host (prod) 改成 apiDomain，導致後續 API 繞過 proxy 跨源打外部 domain。
	axios.defaults.baseURL = apiDomain;

	const success = await new Promise(resolve => {
		const unsubscribe = store.subscribe(() => {
			const state = store.getState();
			const defaultActionDone = defaultActionCalls.actionDone(ctx);
			const themeState = selectors.getThemeState(state);
			if (defaultActionDone) {
				if (themeState === 'NOT_FOUND') {
					res.writeHead(301, { Location: resolveRedirectTarget(domain) });
					res.end();
				} else {
					resolve(unsubscribe);
				}
			}
		});

		defaultActionCalls.actionCall(ctx);
	});
	success();

	return {
		pageProps: {
			// Call page-level getInitialProps
			...(Component.getInitialProps ?
				await Component.getInitialProps(ctx) :
				{}),
			ssrIsMobile,
		},
		domain,
		ssrHeaders,
		statusCode,
		ssrIsMobile,
	};
};

export default reduxWrapper.withRedux(MyApp);
